<template>
  <Teleport to="body">
    <BackPanel v-if="modelValue" @click.self="handleBackPanelClick">
      <div
        class="fixed inset-0 z-[9999] flex items-end justify-center"
        @click.self="handleBackPanelClick"
      >
        <div
          class="w-full bg-white shadow-2xl flex flex-col ease-out relative"
          :class="{ 
            'h-full rounded-none': isFullscreen && !isDragging,
            'h-[50vh]': !isFullscreen && !isDragging,
            'rounded-t-2xl': !isFullscreen || isDragging,
            'transition-modal': !isDragging,
            'dragging': isDragging,
            'transform': isDragging || isTransitioning
          }"
          :style="dynamicModalStyle"
          @click.stop
          @mousedown="handleModalMouseDown"
          @mouseup="handleModalMouseUp"
          ref="mobileModal"
        >
          <!-- Drag Handle - Absolutely positioned relative to modal -->
          <div 
            class="absolute top-0 left-0 right-0 flex justify-center cursor-grab active:cursor-grabbing z-10 py-4"
            @mousedown="startDrag"
            @touchstart="startDrag"
          >
            <div 
              class="bg-gray-300 rounded-full transition-all duration-300"
              :class="isDragging ? 'w-16 h-2' : 'w-10 h-1.5'"
            ></div>
          </div>

          <!-- Content Slot with top padding for drag handle -->
          <div class="flex-1 overflow-hidden pt-12">
            <slot></slot>
          </div>
        </div>
      </div>
    </BackPanel>
  </Teleport>
</template>

<script>
import BackPanel from './BackPanel.vue'

export default {
  name: 'MobileModal',
  components: {
    BackPanel
  },
  emits: ['update:modelValue'],
  props: {
    modelValue: {
      type: Boolean,
      required: true
    },
    velocityThreshold: {
      type: Number,
      default: 0.5
    },
    fullscreenVelocityThreshold: {
      type: Number,
      default: 1.5
    }
  },
  data() {
    return {
      isDragging: false,
      dragOffset: 0,
      startY: 0,
      modalStartHeight: 0,
      isFullscreen: false,
      isTransitioning: false,
      mouseDownOnModal: false,
      velocityTracker: {
        positions: [],
        maxSamples: 5
      }
    }
  },
  computed: {
    dynamicModalStyle() {
      if (!this.isDragging) {
        return {}
      }
      
      const windowHeight = window.innerHeight
      let transform = ''
      let height = ''
      
      // During dragging, always set explicit height to prevent CSS class conflicts
      if (this.isFullscreen) {
        // When in fullscreen, shrink when dragging down
        if (this.dragOffset > 0) {
          const targetNormalHeight = windowHeight * 0.5
          const maxShrink = windowHeight - targetNormalHeight
          const shrinkOffset = Math.min(this.dragOffset, maxShrink)
          const newHeight = windowHeight - shrinkOffset
          
          height = `${Math.max(newHeight, targetNormalHeight)}px`
        } else {
          // Keep fullscreen height when dragging up or not moving
          height = `${windowHeight}px`
        }
      } else {
        // When not in fullscreen, always set the base height to prevent jumping
        height = `${this.modalStartHeight}px`
        
        if (this.dragOffset < 0) {
          // Dragging up - expand toward fullscreen
          const expandOffset = Math.abs(this.dragOffset)
          const maxExpansion = windowHeight - this.modalStartHeight
          const actualExpansion = Math.min(expandOffset, maxExpansion)
          const newHeight = this.modalStartHeight + actualExpansion
          
          height = `${newHeight}px`
        } else {
          // Dragging down - move with transform but keep height stable
          transform = `translateY(${this.dragOffset}px)`
        }
      }
      
      return {
        transform,
        height,
        // Always round top corners during dragging, except when truly fullscreen (height = 100vh) and not moving
        borderRadius: (this.isFullscreen && this.dragOffset <= 0) ? '0' : '16px 16px 0 0'
      }
    }
  },
  methods: {
    handleBackPanelClick(event) {
      // Only close if the mouse wasn't pressed down on the modal and not dragging
      if (!this.mouseDownOnModal && !this.isDragging && event.target === event.currentTarget) {
        this.closeModal()
      }
      // Reset the flag after handling the click
      this.mouseDownOnModal = false
    },
    
    handleModalMouseDown() {
      this.mouseDownOnModal = true
    },
    
    handleModalMouseUp() {
      // Reset the flag when mouse is released on the modal
      // Use a small timeout to ensure click event is processed first
      setTimeout(() => {
        this.mouseDownOnModal = false
      }, 0)
    },
    
    closeModal() {
      this.$emit('update:modelValue', false)
    },
    
    startDrag(event) {
      event.preventDefault()
      this.isDragging = true
      this.dragOffset = 0
      
      // Store initial modal height
      if (this.$refs.mobileModal) {
        this.modalStartHeight = this.$refs.mobileModal.offsetHeight
      }
      
      // Get starting position
      this.startY = event.type === 'touchstart' ? event.touches[0].clientY : event.clientY
      
      // Reset velocity tracker
      this.velocityTracker.positions = []
      
      // Add event listeners
      const moveEvent = event.type === 'touchstart' ? 'touchmove' : 'mousemove'
      const endEvent = event.type === 'touchstart' ? 'touchend' : 'mouseup'
      
      document.addEventListener(moveEvent, this.onDrag, { passive: false })
      document.addEventListener(endEvent, this.endDrag, { once: true })
    },
    
    onDrag(event) {
      if (!this.isDragging) return
      
      event.preventDefault()
      
      const currentY = event.type === 'touchmove' ? event.touches[0].clientY : event.clientY
      this.dragOffset = currentY - this.startY
      
      // Track velocity
      const now = Date.now()
      this.velocityTracker.positions.push({
        y: currentY,
        time: now
      })
      
      // Keep only recent samples
      if (this.velocityTracker.positions.length > this.velocityTracker.maxSamples) {
        this.velocityTracker.positions.shift()
      }
    },
    
    endDrag(event) {
      if (!this.isDragging) return
      
      // Remove event listeners
      const moveEvent = event.type === 'touchend' ? 'touchmove' : 'mousemove'
      document.removeEventListener(moveEvent, this.onDrag)
      
      // Calculate velocity
      let velocity = 0
      if (this.velocityTracker.positions.length >= 2) {
        const recent = this.velocityTracker.positions[this.velocityTracker.positions.length - 1]
        const older = this.velocityTracker.positions[0]
        const timeDiff = recent.time - older.time
        if (timeDiff > 0) {
          velocity = (recent.y - older.y) / timeDiff
        }
      }
      
      // Determine final state based on drag distance and velocity
      const windowHeight = window.innerHeight
      const dragThreshold = windowHeight * 0.25
      
      let shouldClose = false
      let shouldGoFullscreen = false
      
      if (this.isFullscreen) {
        // From fullscreen - be more forgiving, require larger drag or higher velocity to close
        const fullscreenCloseThreshold = windowHeight * 0.4  // 40% of screen height
        
        if (this.dragOffset > fullscreenCloseThreshold && velocity > this.fullscreenVelocityThreshold) {
          shouldClose = true
        } else if (this.dragOffset > dragThreshold || velocity > this.velocityThreshold) {
          // Go to normal size instead of closing
          this.isFullscreen = false
        } else {
          // Stay fullscreen
          this.isFullscreen = true
        }
      } else {
        // From normal size
        if (this.dragOffset > dragThreshold || velocity > this.velocityThreshold) {
          shouldClose = true
        } else if (this.dragOffset < -dragThreshold || velocity < -this.velocityThreshold) {
          shouldGoFullscreen = true
        }
      }
      
      // Apply final state
      if (shouldClose) {
        this.closeModal()
      } else if (shouldGoFullscreen) {
        this.isFullscreen = true
      }
      
      // Reset drag state with transition
      this.isTransitioning = true
      this.isDragging = false
      this.dragOffset = 0
      
      // Clear transition flag after animation
      setTimeout(() => {
        this.isTransitioning = false
      }, 300)
    }
  },
  
  watch: {
    modelValue(newVal) {
      if (!newVal) {
        // Reset state when modal closes
        this.isFullscreen = false
        this.isDragging = false
        this.dragOffset = 0
        this.isTransitioning = false
        this.mouseDownOnModal = false
      }
    }
  }
}
</script>

<style scoped>
.transition-modal {
  transition: height 0.3s cubic-bezier(0.4, 0.0, 0.2, 1), 
              border-radius 0.3s cubic-bezier(0.4, 0.0, 0.2, 1);
}

.dragging {
  transition: none !important;
}

.transform {
  will-change: transform, height;
}
</style>
